// Workspace-scope data hook extracted from Sidebar.tsx during the P5 split.
// Bundles the giant block of useQuery + useMemo derivations that the
// workspace view depends on:
//   - allChannels / allDms (from the sidebar-tree composite, filtered)
//   - workspaceLists (Zero useQuery)
//   - my workspace memberships + role flags
//   - workspace members + collaborator IDs
//   - client spaces (own + linked + collaborator) merged + deduped
//   - archived split
//   - per-space channel map
//
// All `useMemo` deps + the `useQuery` hooks live here verbatim — behavior is
// byte-identical with the inline version. Returns one object the parent
// destructures.

import { useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  computeChannelsWithCollaborators,
  mergeClientSpaces,
} from "./sidebar-workspace-derivations";

interface ChannelLike {
  id: string;
  name: string;
  workspace_id?: string | null;
  type?: string | null;
  is_archived?: boolean | null;
  // Post-migration 120 a space is a channel (type='space') and its child
  // channels link back via `parent_id` (the self-FK that replaced the legacy
  // `client_space_id` column, which never existed on `channels`). The standalone
  // channel list filters on this to keep space-owned children out.
  parent_id?: string | null;
  members?: ReadonlyArray<{ user_id: string }> | null;
}

interface MembershipLike {
  id: string;
  channel_id: string;
  is_channel_manager?: boolean | null;
  hidden?: boolean | null;
}

interface UseSidebarWorkspaceDataInput {
  selectedWorkspace: string | null;
  user: { id: string } | null | undefined;
  channelMap: Map<string, ChannelLike>;
  membershipMap: Map<string, MembershipLike>;
  myMembers: MembershipLike[];
}

export function useSidebarWorkspaceData({
  selectedWorkspace,
  user,
  channelMap,
  membershipMap,
  myMembers,
}: UseSidebarWorkspaceDataInput) {
  // D.1: workspace-scope channels — was a useQuery against
  // `queries.channels.byWorkspaceAndUserWithMembers`. Now derived from the
  // composite's channelMap filtered by workspace_id. Type filter excludes
  // DMs (workspace_id IS NULL normally, so the workspace_id equality
  // filters them anyway — explicit type filter is defense in depth).
  const allChannels = useMemo(() => {
    if (!selectedWorkspace) return [] as ChannelLike[];
    const out: ChannelLike[] = [];
    for (const c of channelMap.values()) {
      if (c.workspace_id !== selectedWorkspace) continue;
      if (c.type && c.type !== "channel") continue; // skip DMs/group_dms
      out.push(c);
    }
    return out;
  }, [channelMap, selectedWorkspace]);

  // D.1: all my DMs (was two separate useQuery calls for workspace-scoped
  // and home-scoped DMs). Matches `dmsByUserWithMembers` semantics:
  //   - type IN (dm, group_dm)
  //   - is_archived = false
  //   - my membership has hidden = false
  // Used for the sidebar's preload set; workspace-scope filtering (if
  // needed for rendering) happens inside DMList / its consumers.
  const allDms = useMemo(() => {
    const out: ChannelLike[] = [];
    for (const c of channelMap.values()) {
      if (c.type !== "dm" && c.type !== "group_dm") continue;
      if (c.is_archived) continue;
      const myMembership = membershipMap.get(c.id);
      if (!myMembership || myMembership.hidden) continue;
      out.push(c);
    }
    return out;
  }, [channelMap, membershipMap]);

  const [workspaceListsRaw] = useQuery(
    selectedWorkspace ? queries.lists.byWorkspace(selectedWorkspace) : null,
  );
  const workspaceLists = useMemo(
    () => workspaceListsRaw ?? [],
    [workspaceListsRaw],
  );

  const channelById = useMemo(() => {
    const map = new Map<string, (typeof allChannels)[number]>();
    for (const ch of allChannels) {
      map.set(ch.id, ch);
    }
    return map;
  }, [allChannels]);
  const archivedChannels = useMemo(
    () =>
      selectedWorkspace
        ? allChannels.filter(
            (ch) => (!ch.type || ch.type === "channel") && ch.is_archived,
          )
        : [],
    [selectedWorkspace, allChannels],
  );

  const managedChannelIds = useMemo(() => {
    const managed = new Set<string>();
    for (const member of myMembers) {
      if (member.is_channel_manager) {
        managed.add(member.channel_id);
      }
    }
    return managed;
  }, [myMembers]);

  const [myWorkspaceMembershipsRaw, myWsMembershipsStatus] = useQuery(
    user ? queries.workspace_members.byUser() : null,
  );
  const myWorkspaceMemberships = useMemo(
    () => myWorkspaceMembershipsRaw ?? [],
    [myWorkspaceMembershipsRaw],
  );
  // Membership map for home scope role badges
  const homeMembershipMap = useMemo(() => {
    const map = new Map<string, (typeof myWorkspaceMemberships)[number]>();
    for (const m of myWorkspaceMemberships) {
      map.set(m.workspace_id, m);
    }
    return map;
  }, [myWorkspaceMemberships]);

  // Always fetch when workspace is selected — needed for collaboratorIds and Handshake icons
  const [wsMembersRaw] = useQuery(
    selectedWorkspace
      ? queries.workspace_members.byWorkspace(selectedWorkspace)
      : null,
  );
  const wsMembers = useMemo(() => wsMembersRaw ?? [], [wsMembersRaw]);
  const collaboratorIds = useMemo(
    () =>
      new Set(
        wsMembers
          .filter((m) => m.role === "collaborator")
          .map((m) => m.user_id),
      ),
    [wsMembers],
  );
  const wsMemberIdSet = useMemo(
    () =>
      new Set(
        wsMembers
          .filter((m) => m.role !== "collaborator")
          .map((m) => m.user_id),
      ),
    [wsMembers],
  );

  const channelsWithCollaborators = useMemo(
    () => computeChannelsWithCollaborators(allChannels, collaboratorIds),
    [allChannels, collaboratorIds],
  );

  // Post mig 120: `byWorkspaceWithChannels` and `byUserMembership` are
  // identical queries (both gate on space-membership only — the legacy
  // pre-mig distinction was that one read `client_spaces` and the other
  // read `client_space_members`, which collapsed into the same `channels` /
  // `channel_members` shape). The conditional collaborator-only call to
  // `byUserMembership` was therefore a wasted Zero subscription (results
  // dedup'd in mergeClientSpaces by id). Drop it; pass `null` to the
  // `mergeClientSpaces` 3rd arg to keep the helper signature stable
  // alongside its existing tests. PR #1250 round-4 cleanup.
  const [ownSpacesRaw] = useQuery(
    selectedWorkspace
      ? queries.client_spaces.byWorkspaceWithChannels(selectedWorkspace)
      : null,
  );
  const [linkedSpacesRaw] = useQuery(
    selectedWorkspace
      ? queries.client_spaces.byLinkedWorkspace(selectedWorkspace)
      : null,
  );
  const clientSpaces = useMemo(
    () => mergeClientSpaces(ownSpacesRaw, linkedSpacesRaw, null),
    [ownSpacesRaw, linkedSpacesRaw],
  );

  // Exclude space-owned channels from the main channels list
  const channelList = useMemo(
    () =>
      selectedWorkspace
        ? allChannels.filter(
            (ch) =>
              (!ch.type || ch.type === "channel") &&
              !ch.is_archived &&
              !ch.parent_id,
          )
        : [],
    [selectedWorkspace, allChannels],
  );

  // Check if current user is workspace admin/owner
  const currentUserWsMembership = useMemo(
    () =>
      myWorkspaceMemberships.find((m) => m.workspace_id === selectedWorkspace),
    [myWorkspaceMemberships, selectedWorkspace],
  );
  const isWorkspaceAdmin = useMemo(() => {
    const role = currentUserWsMembership?.role;
    return role === "primary_owner" || role === "owner" || role === "admin";
  }, [currentUserWsMembership]);
  const isCollaborator = currentUserWsMembership?.role === "collaborator";

  // Check if user can manage a specific channel (workspace admin or channel manager)
  const canManageChannel = (channelId: string) => {
    if (isWorkspaceAdmin) return true;
    return managedChannelIds.has(channelId);
  };

  // Check if user can delete channels (workspace owner only)
  const canDeleteChannel = useMemo(() => {
    const role = currentUserWsMembership?.role;
    return role === "primary_owner" || role === "owner";
  }, [currentUserWsMembership]);

  // Use space.child_channels directly — the .related("child_channels")
  // query returns full child-channel data (post mig 120 the relation is
  // named `child_channels`, was `channels` pre-mig). We must NOT
  // cross-reference channelById here because it only contains channels
  // from the selected workspace, while space child channels belong to
  // the host workspace (which differs for linked-workspace users).
  type SpaceChannel = NonNullable<
    (typeof clientSpaces)[number]["child_channels"]
  >[number];
  const spaceChannelMap = useMemo(() => {
    const map = new Map<string, SpaceChannel[]>();
    for (const space of clientSpaces) {
      const channels = (space.child_channels ?? [])
        .filter((ch: { is_archived?: boolean | null }) => !ch.is_archived)
        .sort((a: { name: string }, b: { name: string }) =>
          a.name.localeCompare(b.name),
        );
      map.set(space.id, channels);
    }
    return map;
  }, [clientSpaces]);

  return {
    allChannels,
    allDms,
    workspaceLists,
    channelById,
    archivedChannels,
    managedChannelIds,
    myWorkspaceMemberships,
    myWsMembershipsStatus,
    homeMembershipMap,
    wsMembers,
    collaboratorIds,
    wsMemberIdSet,
    channelsWithCollaborators,
    clientSpaces,
    channelList,
    currentUserWsMembership,
    isWorkspaceAdmin,
    isCollaborator,
    canManageChannel,
    canDeleteChannel,
    spaceChannelMap,
  };
}
